/**
 * Split form hint / instruction copy into labelled blocks so the Hints
 * panel can render titled cards instead of one run-on blob.
 *
 * Real N-400 / I-90 copy (from GET /forms/{id}/questions) looks like:
 *   - `**What should I enter?**\n…` on most N-400 questions
 *   - `**NOTE:** …` on I-90 notes
 *   - ATX headings or bold-only title lines on longer option explanations
 *   - Long unlabelled section descriptions (often PDF line-wrapped) with
 *     blank-line paragraphs
 *
 * Short unlabelled notes (e.g. `(mm/dd/yyyy)`) stay a single block.
 */

export type HintBlock = {
  /** Display label, or null for a plain unlabelled paragraph. */
  label: string | null;
  /** Markdown body that follows the label. */
  body: string;
};

const ATX_HEADING = /^(#{1,6})\s+(.+?)\s*$/;
const BOLD_TITLE_ONLY = /^\*\*([^*\n]+?)\*\*\s*$/;
const BOLD_NOTE = /^\*\*NOTE:\*\*\s*([\s\S]*)$/i;
const PLAIN_NOTE = /^NOTE:\s+([\s\S]*)$/i;

/** Only split unlabelled copy into paragraph cards once it is clearly long. */
const LONG_UNLABELLED_CHARS = 400;

function normalizeLabel(raw: string): string {
  const trimmed = raw.replace(/:+$/, "").trim();
  if (/^note$/i.test(trimmed)) return "Note";
  return trimmed;
}

function isListLine(line: string): boolean {
  return /^\s*(?:[-*+]|\d+[.)])\s+/.test(line);
}

/**
 * Parse hint/instruction markdown into discrete labelled blocks.
 */
export function parseHintBlocks(raw?: string | null): HintBlock[] {
  if (!raw || typeof raw !== "string" || !raw.trim()) return [];

  const lines = raw.replace(/\r\n/g, "\n").trim().split("\n");
  const blocks: HintBlock[] = [];
  let current: HintBlock | null = null;

  const flush = () => {
    if (!current) return;
    const body = current.body.replace(/^\n+/, "").replace(/\n+$/, "");
    if (current.label || body) {
      blocks.push({ label: current.label, body });
    }
    current = null;
  };

  const start = (label: string | null, firstBody = "") => {
    flush();
    current = { label, body: firstBody };
  };

  const append = (line: string) => {
    if (!current) current = { label: null, body: "" };
    current.body = current.body ? `${current.body}\n${line}` : line;
  };

  for (const line of lines) {
    const atx = line.match(ATX_HEADING);
    if (atx) {
      start(normalizeLabel(atx[2]));
      continue;
    }

    const boldTitle = line.match(BOLD_TITLE_ONLY);
    if (boldTitle) {
      start(normalizeLabel(boldTitle[1]));
      continue;
    }

    const boldNote = line.match(BOLD_NOTE);
    if (boldNote) {
      start("Note", boldNote[1]);
      continue;
    }

    const plainNote = line.match(PLAIN_NOTE);
    if (plainNote && !isListLine(line)) {
      start("Note", plainNote[1]);
      continue;
    }

    append(line);
  }
  flush();

  return splitLongUnlabelled(blocks);
}

function splitLongUnlabelled(blocks: HintBlock[]): HintBlock[] {
  if (blocks.length !== 1 || blocks[0].label) return blocks;
  const { body } = blocks[0];
  if (body.length < LONG_UNLABELLED_CHARS) return blocks;
  const paragraphs = body
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (paragraphs.length < 2) return blocks;
  return paragraphs.map((part) => ({ label: null, body: part }));
}
